/**
 * `<NotesTable>` — the interactive "My Notes" table (2026-06-23).
 *
 * A fully dynamic, server-driven table: sort by title / created / updated (clickable headers),
 * filter by title search, tag (multi, ltree-subtree), notebook, source, and date range, with
 * infinite-scroll pagination. Everything is server-side (the `GET /api/v1/notes` endpoint) so it
 * scales to the platform's 100K-note ceiling — the page never loads the whole corpus client-side.
 *
 * Mounted as the primary content of the editor's empty-state dashboard (`<NotePoolMetrics>`).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import {
  fetchNotesTable,
  type NotesTableRowDTO,
  type NotesTableOrderBy,
  type NotesTableDirection,
} from '@/api';
import { VirtualizedTable, type TableColumn } from '@/components';
import { useTagListQuery, useNavigateToNote } from '@/hooks';
import { useListNotebooksQuery, resolveNotebookPath } from '@/hooks/useNotebookQueries';
import { asNoteId, asNotebookId } from '@/types';
import './notesTable.css';

const PAGE_SIZE = 50;
/** The note sources, for the source filter (mirrors the backend SourceType vocabulary). */
const SOURCES = ['plaud', 'joplin', 'manual', 'markdown_import', 'paste', 'chat', 'email'];

export function NotesTable(): JSX.Element {
  const goToNote = useNavigateToNote();

  // -- filter + sort state ------------------------------------------------------------------
  const [orderBy, setOrderBy] = useState<NotesTableOrderBy>('updated');
  const [direction, setDirection] = useState<NotesTableDirection>('desc');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [notebookId, setNotebookId] = useState<string>('');
  const [sources, setSources] = useState<string[]>([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Debounce the title search so we don't refetch on every keystroke.
  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(t);
  }, [search]);

  // -- filter option sources ----------------------------------------------------------------
  const { items: tagItems } = useTagListQuery({ minCount: 1 });
  const notebooksList = useListNotebooksQuery();
  const notebooks = useMemo(() => notebooksList.data?.items ?? [], [notebooksList.data]);
  // U7 (2026-07-05): the cell truncates its TAIL, which used to eat the informative
  // leaf segment ("10 Active Projects / D…"). Return the raw segments so the cell can
  // render leaf-FIRST ("Meeting Notes · 10 Active Projects / …") with the full
  // root-to-leaf path on hover.
  const nbSegments = useCallback(
    (id: string | null): string[] =>
      id ? resolveNotebookPath(asNotebookId(id), notebooks) : [],
    [notebooks],
  );

  // -- data ---------------------------------------------------------------------------------
  const query = useInfiniteQuery({
    queryKey: [
      'notes-table',
      orderBy,
      direction,
      debouncedSearch,
      tags,
      notebookId,
      sources,
      dateFrom,
      dateTo,
    ],
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      fetchNotesTable({
        orderBy,
        direction,
        q: debouncedSearch || undefined,
        tags,
        notebookIds: notebookId ? [notebookId] : [],
        sources,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        limit: PAGE_SIZE,
        offset: pageParam as number,
      }),
    getNextPageParam: (last, all) => {
      const loaded = all.reduce((n, p) => n + p.items.length, 0);
      return loaded < last.total ? loaded : undefined;
    },
  });

  const rows = useMemo(() => query.data?.pages.flatMap((p) => p.items) ?? [], [query.data]);
  const total = query.data?.pages[0]?.total ?? 0;

  const onEndReached = useCallback(() => {
    if (query.hasNextPage && !query.isFetchingNextPage) void query.fetchNextPage();
  }, [query]);

  // -- sort header -------------------------------------------------------------------------
  const toggleSort = useCallback(
    (col: NotesTableOrderBy) => {
      if (orderBy === col) {
        setDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
      } else {
        setOrderBy(col);
        setDirection(col === 'title' ? 'asc' : 'desc'); // names A→Z; dates newest-first
      }
    },
    [orderBy],
  );
  const sortHeader = (label: string, col: NotesTableOrderBy): JSX.Element => (
    <button
      type="button"
      className={'notes-table__sort' + (orderBy === col ? ' notes-table__sort--active' : '')}
      onClick={() => toggleSort(col)}
    >
      {label}
      <span className="notes-table__sort-arrow">
        {orderBy === col ? (direction === 'asc' ? '▲' : '▼') : ''}
      </span>
    </button>
  );

  const columns: TableColumn<NotesTableRowDTO>[] = [
    {
      id: 'title',
      header: sortHeader('Title', 'title'),
      width: 'minmax(220px, 2.2fr)',
      cell: (r) => <span className="notes-table__title">{r.title || 'Untitled note'}</span>,
    },
    {
      id: 'tags',
      header: 'Tags',
      width: 'minmax(140px, 1.4fr)',
      cell: (r) => (
        <span className="notes-table__tags">
          {r.tags.slice(0, 4).map((t) => (
            <span key={t} className="notes-table__tag">
              #{t}
            </span>
          ))}
        </span>
      ),
    },
    {
      id: 'notebook',
      header: 'Notebook',
      width: 'minmax(120px, 1fr)',
      cell: (r) => {
        const segs = nbSegments(r.notebookId);
        if (segs.length === 0) return <span className="notes-table__muted">—</span>;
        const leaf = segs[segs.length - 1];
        const parents = segs.slice(0, -1).join(' / ');
        return (
          <span className="notes-table__muted" title={segs.join(' / ')}>
            {leaf}
            {parents !== '' && (
              <span className="notes-table__nb-parents"> · {parents}</span>
            )}
          </span>
        );
      },
    },
    {
      id: 'source',
      header: 'Source',
      width: '92px',
      cell: (r) => <span className="notes-table__muted">{r.source}</span>,
    },
    {
      id: 'updated',
      header: sortHeader('Updated', 'updated'),
      width: '108px',
      cell: (r) => <span className="notes-table__muted">{formatRelative(r.updatedAt)}</span>,
    },
    {
      id: 'created',
      header: sortHeader('Created', 'created'),
      width: '104px',
      cell: (r) => <span className="notes-table__muted">{formatShortDate(r.createdAt)}</span>,
    },
  ];

  const hasFilters =
    debouncedSearch !== '' ||
    tags.length > 0 ||
    notebookId !== '' ||
    sources.length > 0 ||
    dateFrom !== '' ||
    dateTo !== '';
  const clearAll = (): void => {
    setSearch('');
    setTags([]);
    setNotebookId('');
    setSources([]);
    setDateFrom('');
    setDateTo('');
  };

  return (
    <section className="notes-table-surface">
      <div className="notes-table__bar">
        <input
          type="search"
          className="notes-table__search"
          placeholder="Search titles…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search note titles"
        />

        {/* Tag filter — add via the select, remove via the chips. */}
        <select
          className="notes-table__select"
          value=""
          onChange={(e) => {
            const v = e.target.value;
            if (v && !tags.includes(v)) setTags((prev) => [...prev, v]);
          }}
          aria-label="Add tag filter"
        >
          <option value="">+ Tag…</option>
          {tagItems
            .filter((t) => !tags.includes(t.path))
            .map((t) => (
              <option key={t.tagId} value={t.path}>
                #{t.display} ({t.noteCount})
              </option>
            ))}
        </select>

        <select
          className="notes-table__select"
          value={notebookId}
          onChange={(e) => setNotebookId(e.target.value)}
          aria-label="Filter by notebook"
        >
          <option value="">All notebooks</option>
          {notebooks.map((nb) => (
            <option key={nb.notebookId} value={nb.notebookId}>
              {nb.title}
            </option>
          ))}
        </select>

        <input
          type="date"
          className="notes-table__date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          aria-label="From date"
          title="From date"
        />
        <input
          type="date"
          className="notes-table__date"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          aria-label="To date"
          title="To date"
        />

        <span className="notes-table__count">
          {query.isLoading ? 'Loading…' : `${total.toLocaleString()} notes`}
        </span>
        {hasFilters && (
          <button type="button" className="notes-table__clear" onClick={clearAll}>
            Clear
          </button>
        )}
      </div>

      {/* Source toggle chips + active tag chips. */}
      <div className="notes-table__chips">
        {SOURCES.map((s) => (
          <button
            key={s}
            type="button"
            className={
              'notes-table__chip' + (sources.includes(s) ? ' notes-table__chip--on' : '')
            }
            onClick={() =>
              setSources((prev) =>
                prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s],
              )
            }
          >
            {s}
          </button>
        ))}
        {tags.map((t) => (
          <button
            key={t}
            type="button"
            className="notes-table__chip notes-table__chip--tag"
            onClick={() => setTags((prev) => prev.filter((x) => x !== t))}
            title="Remove tag filter"
          >
            #{t} ✕
          </button>
        ))}
      </div>

      <div className="notes-table__viewport">
        {query.isError ? (
          <div className="notes-table__empty" style={{ color: 'var(--bad)' }}>
            Couldn’t load notes: {String(query.error)}
          </div>
        ) : rows.length === 0 && !query.isLoading ? (
          <div className="notes-table__empty">No notes match these filters.</div>
        ) : (
          <VirtualizedTable<NotesTableRowDTO>
            columns={columns}
            rows={rows}
            getKey={(r) => r.noteId}
            ariaLabel="My notes"
            onEndReached={onEndReached}
            onRowClick={(r) => goToNote({ noteId: asNoteId(r.noteId) })}
          />
        )}
      </div>
    </section>
  );
}

// --- date formatting (matches the editor's meta row) -----------------------------------------

function formatRelative(iso: string): string {
  try {
    const diffMs = Date.now() - new Date(iso).getTime();
    if (diffMs < 60_000) return 'just now';
    const m = Math.round(diffMs / 60_000);
    if (m < 60) return `${m}m ago`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.round(h / 24);
    if (d < 7) return `${d}d ago`;
    if (d < 30) return `${Math.round(d / 7)}w ago`;
    if (d < 365) return `${Math.round(d / 30)}mo ago`;
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
}

function formatShortDate(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  const d = new Date(t);
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
}
